/**
 * The server's word that the log grew, the moment it does (the owner, 2026-10-08: an order set
 * aside on one phone known on the others «within about a second»).
 *
 * While the app is open, in sight and online, the phone keeps `/api/sync/escuchar` open, and each
 * number it hears above what it has pulled is a sync at once instead of at the next half-minute
 * poll, which stays for whatever the stream misses. Only the number comes this way: the records
 * come by the pull, as ever, so a stream that drops loses nothing but time. Out of sight, offline
 * or practising, it is closed; a dropped one is opened again, waiting longer each time it fails.
 */

import { getMeta } from '../db/dexie.js';
import { enPractica } from '../lib/practica.js';
import { sincronizar } from './engine.js';

/** One event of the stream: its name and its data. */
export interface EventoSSE {
  evento: string;
  datos: string;
}

/**
 * What has arrived, in whole events, and what is left of one still coming. Comments, a line that
 * starts with «:» (the server's beat), say nothing. Pure, for the tests.
 */
export function partirSSE(texto: string): { eventos: EventoSSE[]; resto: string } {
  const bloques = texto.split(/\r\n\r\n|\n\n/);
  const resto = bloques.pop() ?? '';
  const eventos: EventoSSE[] = [];
  for (const bloque of bloques) {
    let evento = 'message';
    const datos: string[] = [];
    for (const linea of bloque.split(/\r\n|\n/)) {
      if (linea === '' || linea.startsWith(':')) continue;
      const i = linea.indexOf(':');
      const campo = i < 0 ? linea : linea.slice(0, i);
      const valor = i < 0 ? '' : linea.slice(i + 1).replace(/^ /, '');
      if (campo === 'event') evento = valor;
      else if (campo === 'data') datos.push(valor);
    }
    if (datos.length > 0) eventos.push({ evento, datos: datos.join('\n') });
  }
  return { eventos, resto };
}

const ESPERA_MIN_MS = 1_000;
const ESPERA_MAX_MS = 30_000;
/** Not a byte in this long, beats included, and the line is dead: the server beats every 25 s. */
const SILENCIO_MAX_MS = 60_000;

/** Listens until the answer is called. */
export function iniciarEscucha(): () => void {
  let activa = true;
  /** The stream open or opening, to close it when the app goes out of sight. */
  let conexion: AbortController | null = null;
  let reintento: ReturnType<typeof setTimeout> | undefined;
  let espera = ESPERA_MIN_MS;

  const conviene = () => activa && !enPractica() && navigator.onLine && document.visibilityState === 'visible';

  async function alNumero(seq: number): Promise<void> {
    if (Number.isFinite(seq) && seq > ((await getMeta('ultimoServerSeq')) ?? 0)) void sincronizar();
  }

  function programar(): void {
    clearTimeout(reintento);
    if (!conviene()) return;
    reintento = setTimeout(() => void conectar(), espera);
    espera = Math.min(espera * 2, ESPERA_MAX_MS);
  }

  async function conectar(): Promise<void> {
    if (conexion || !conviene()) return;
    const corte = new AbortController();
    conexion = corte;
    let vigia: ReturnType<typeof setInterval> | undefined;
    try {
      const token = await getMeta('token');
      if (!token) return;
      const res = await fetch('/api/sync/escuchar', {
        headers: { authorization: `Bearer ${token}`, accept: 'text/event-stream' },
        cache: 'no-store',
        signal: corte.signal,
      });
      if (!res.ok || !res.body) return;
      espera = ESPERA_MIN_MS;
      let ultimoByte = Date.now();
      vigia = setInterval(() => {
        if (Date.now() - ultimoByte > SILENCIO_MAX_MS) corte.abort();
      }, SILENCIO_MAX_MS / 4);
      const lector = res.body.getReader();
      const texto = new TextDecoder();
      let resto = '';
      for (;;) {
        const { done, value } = await lector.read();
        if (done) break;
        ultimoByte = Date.now();
        const partes = partirSSE(resto + texto.decode(value, { stream: true }));
        resto = partes.resto;
        for (const e of partes.eventos) if (e.evento === 'seq') await alNumero(Number(e.datos));
      }
    } catch {
      // Dropped, offline, or closed on purpose: whether to open it again is decided below.
    } finally {
      clearInterval(vigia);
      if (conexion === corte) conexion = null;
      // Closed on purpose, out of sight or offline: it opens again when that changes.
      if (!corte.signal.aborted || conviene()) programar();
    }
  }

  function cerrar(): void {
    clearTimeout(reintento);
    conexion?.abort();
    conexion = null;
  }

  const alCambiar = () => {
    if (conviene()) {
      espera = ESPERA_MIN_MS;
      void conectar();
    } else cerrar();
  };
  window.addEventListener('online', alCambiar);
  window.addEventListener('offline', alCambiar);
  document.addEventListener('visibilitychange', alCambiar);
  void conectar();

  return () => {
    activa = false;
    cerrar();
    window.removeEventListener('online', alCambiar);
    window.removeEventListener('offline', alCambiar);
    document.removeEventListener('visibilitychange', alCambiar);
  };
}
